/**
 * After a deployment, hashed code-split chunks from the prior build disappear from
 * the server (`emptyOutDir` in Vite) while tabs that stayed open still run an old
 * `index.js` that references old chunk filenames. Lazily-loaded routes such as `/plans`
 * then throw "Failed to fetch dynamically imported module".
 *
 * A single full reload fetches fresh `index.js` + chunks. Debounced so a genuine
 * misconfiguration does not reload-loop.
 */

const STORAGE_KEY = "__fg_fp_chunk_reload_ts";
const DEBOUNCE_MS = 4500;

function isStaleChunkLoadError(reason: unknown): boolean {
  const msg =
    typeof reason === "string"
      ? reason
      : reason instanceof Error
        ? `${reason.message}\n${reason.stack ?? ""}`
        : "";

  const lower = msg.toLowerCase();
  return (
    lower.includes("failed to fetch dynamically imported module") ||
    lower.includes("error loading dynamically imported module") ||
    lower.includes("importing a module script failed")
  );
}

export function isStaleChunkOrImportFailure(reason: unknown): boolean {
  return isStaleChunkLoadError(reason);
}

/** Best-effort: reload once unless we already attempted very recently (same tab). */
export function tryReloadStaleBuild(): void {
  if (typeof window === "undefined" || typeof sessionStorage === "undefined") return;
  const now = Date.now();
  const last = Number(sessionStorage.getItem(STORAGE_KEY) ?? "0") || 0;
  if (now - last < DEBOUNCE_MS) return;
  sessionStorage.setItem(STORAGE_KEY, String(now));
  window.location.reload();
}

/**
 * Registers global listeners once. Safe to call at module scope in `main.tsx`.
 */
export function registerStaleChunkAutoReload(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("unhandledrejection", (ev) => {
    if (!isStaleChunkLoadError(ev.reason)) return;
    ev.preventDefault();
    tryReloadStaleBuild();
  });
}
